import React, { useState } from 'react'
import { PrinterIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'

export default function MeasurementSheet() {
  const [selectedUnit, setSelectedUnit] = useState<'inches' | 'cm'>('inches')

  const handlePrint = () => {
    window.print()
  }

  const generatePrintableHTML = (unit: 'inches' | 'cm') => {
    const isInches = unit === 'inches'
    const maxH = isInches ? 7 : 18  // 7 inches wide for better camera framing
    const maxV = isInches ? 9 : 23  // 9 inches tall for better camera framing
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Cookie Cutter Measurement Sheet</title>
          <style>
            @page {
              size: letter;
              margin: 0.5in;
            }
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }
            .ruler-container {
              width: 100%;
              height: 100%;
              position: relative;
            }
            .horizontal-ruler, .vertical-ruler {
              position: absolute;
              background: #000;
            }
            .horizontal-ruler {
              height: 2px;
              width: ${maxH}in;
              top: 1in;
              left: 0.75in;
            }
            .vertical-ruler {
              width: 2px;
              height: ${maxV}in;
              top: 1in;
              left: 0.75in;
            }
            .tick {
              position: absolute;
              background: #000;
            }
            .tick-major {
              width: 2px;
              height: 15px;
            }
            .tick-minor {
              width: 1px;
              height: 8px;
            }
            .tick-label {
              position: absolute;
              font-size: 10px;
              font-weight: bold;
            }
            .grid-line {
              position: absolute;
              border: 1px dashed #ccc;
            }
          </style>
        </head>
        <body>
          <div class="ruler-container">
            <!-- Horizontal ruler -->
            <div class="horizontal-ruler"></div>
            
            <!-- Vertical ruler -->
            <div class="vertical-ruler"></div>
            
            <!-- Generate ruler marks -->
            ${Array.from({length: maxH + 1}, (_, i) => `
              <div class="tick tick-major" style="left: ${0.75 + i}in; top: 1in; margin-top: -15px;"></div>
              ${i > 0 ? `<div class="tick-label" style="left: ${0.75 + i}in; top: 0.8in; margin-left: -5px;">${i}</div>` : ''}
              ${isInches && i < maxH ? Array.from({length: 3}, (_, j) => `
                <div class="tick tick-minor" style="left: ${0.75 + i + (j + 1) * 0.25}in; top: 1in; margin-top: -8px;"></div>
              `).join('') : ''}
            `).join('')}
            
            ${Array.from({length: maxV + 1}, (_, i) => `
              <div class="tick tick-major" style="left: 0.75in; top: ${1 + i}in; margin-left: -15px; width: 15px; height: 2px;"></div>
              ${i > 0 ? `<div class="tick-label" style="left: 0.55in; top: ${1 + i}in; margin-top: -5px;">${i}</div>` : ''}
              ${isInches && i < maxV ? Array.from({length: 3}, (_, j) => `
                <div class="tick tick-minor" style="left: 0.75in; top: ${1 + i + (j + 1) * 0.25}in; margin-left: -8px; width: 8px; height: 1px;"></div>
              `).join('') : ''}
            `).join('')}
            
            <!-- Grid lines -->
            ${Array.from({length: maxH}, (_, i) => `
              <div class="grid-line" style="left: ${1.75 + i}in; top: 1in; height: ${maxV}in; border-left: 1px dashed #ddd; border-right: none; border-top: none; border-bottom: none;"></div>
            `).join('')}
            
            ${Array.from({length: maxV}, (_, i) => `
              <div class="grid-line" style="left: 0.75in; top: ${2 + i}in; width: ${maxH}in; border-top: 1px dashed #ddd; border-left: none; border-right: none; border-bottom: none;"></div>
            `).join('')}
          </div>
          
          <div style="position: fixed; bottom: 1.2in; right: 0in; width: 2.5in; font-size: 8px; color: #666;">
            <div style="font-weight: bold; margin-bottom: 4px;">How to use:</div>
            <div>1. Print on standard 8.5" × 11" paper</div>
            <div>2. Place cookie cutters on grid</div>
            <div>3. Take photos from directly above</div>
            <div>4. Use for size reference in inventory</div>
            <div style="margin-top: 6px; font-size: 7px; color: #999;">Generated by The Cutter Keeper</div>
          </div>
        </body>
      </html>
    `
  }

  const handleDownload = async () => {
    try {
      const htmlContent = generatePrintableHTML(selectedUnit)
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank')
      if (!printWindow) return

      printWindow.document.write(htmlContent)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    } catch (error) {
      console.error('Download error:', error)
      alert('Failed to generate measurement sheet')
    }
  }

  return (
    <div className="print:p-0">
      {/* Header - Hidden when printing */}
      <div className="mb-8 print:hidden">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-bold text-gray-900">Measurement Sheet Generator</h1>
            <p className="mt-2 text-sm text-gray-700">
              Generate a printable ruler sheet for measuring cookie cutters in photos. 
              Print this sheet and place cookie cutters on it when photographing for size reference.
            </p>
          </div>
        </div>
      </div>

      {/* Controls - Hidden when printing */}
      <div className="bg-white shadow rounded-lg mb-8 print:hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
                Unit of Measurement
              </h3>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    className="form-radio h-4 w-4 text-blue-600"
                    name="unit"
                    value="inches"
                    checked={selectedUnit === 'inches'}
                    onChange={(e) => setSelectedUnit(e.target.value as 'inches' | 'cm')}
                  />
                  <span className="ml-2 text-sm text-gray-700">Inches</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    className="form-radio h-4 w-4 text-blue-600"
                    name="unit"
                    value="cm"
                    checked={selectedUnit === 'cm'}
                    onChange={(e) => setSelectedUnit(e.target.value as 'inches' | 'cm')}
                  />
                  <span className="ml-2 text-sm text-gray-700">Centimeters</span>
                </label>
              </div>
            </div>
            
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Download
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print Sheet
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Info */}
      <div className="flex justify-center">
        <div className="bg-white shadow rounded-lg p-8 text-center max-w-md">
          <div className="text-4xl mb-4">📏</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {selectedUnit === 'inches' ? '6" × 8" Ruler Grid' : '15cm × 20cm Ruler Grid'}
          </h3>
          <p className="text-sm text-gray-600">
            The measurement sheet will be generated as a properly scaled PDF with precise {selectedUnit === 'inches' ? 'inch' : 'centimeter'} markings for accurate printing.
          </p>
        </div>
      </div>

      {/* Instructions - Hidden when printing */}
      <div className="mt-8 bg-white shadow rounded-lg print:hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
            How to Use the Measurement Sheet
          </h3>
          <div className="space-y-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-500 text-white text-sm font-medium">
                  1
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-700">
                  <strong>Print the measurement sheet</strong> on standard 8.5" × 11" paper using your printer's normal settings.
                </p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-500 text-white text-sm font-medium">
                  2
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-700">
                  <strong>Position cookie cutters</strong> on the grid area of the printed sheet, ensuring they're within the measurement boundaries.
                </p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-500 text-white text-sm font-medium">
                  3
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-700">
                  <strong>Take clear photos</strong> of the cookie cutters positioned on the measurement grid from directly above.
                </p>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-500 text-white text-sm font-medium">
                  4
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-700">
                  <strong>Add to inventory</strong> using these photos when cataloging your cookie cutters for accurate size reference.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> For best results, ensure good lighting and take photos from directly overhead. 
              The ruler markings will help you and others quickly identify the size of each cookie cutter.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}